// Studio app — root. CD owns the whole app (shell + screens); this is the
// deployable frontend. Data comes from @v0y4ge/api-client; design system from
// @v0y4ge/jrni-ui + jrni-tokens (CD-owned). No static seed data in the render path.

import "@v0y4ge/jrni-tokens/tokens.css";
import "@v0y4ge/jrni-tokens/reset.css";
// App-layout rules (route-shell max-width override) — deliberately OUTSIDE the
// token stylesheets so DS re-emits can never delete them (DS round-trip ①).
import "./styles/studio-app.css";
import { useEffect, useRef } from "react";
import { NavProvider, useNav, isPublicAttendRoute, isPublicHost } from "./shell/router";
import { AppRouter } from "./shell/AppRouter";
import { ToastHost } from "./shell/toast";
import { useAuth, signIn, landingResolved, getUser } from "./shell/access";
import { resolveLanding } from "./shell/navProfiles";
import { ensureTenantContext } from "./lib/tenant-vocab";
import Signin from "./shell/Signin";
import { ImpersonationBanner } from "./shell/ImpersonationBanner";
import { useDateTimeVersion } from "./lib/datetime";
import { useStaffTimezoneSync } from "./screens/staff/useStaffTimezone";
import { ErrorBoundary } from "./shell/ErrorBoundary";

// Deep-link params captured at MODULE LOAD — before <NavProvider> mounts and
// rewrites the URL to a route (which would drop our fragment). Read from the
// hash (survives the preview host's path rewrite) with a query fallback.
const DEEPLINK: { persona: string; route: string | null } | null = (function () {
  if (typeof window === "undefined") return null;
  const raw = (window.location.hash || "").replace(/^#/, "") || (window.location.search || "").replace(/^\?/, "");
  const q = new URLSearchParams(raw);
  const persona = q.get("persona");
  return persona ? { persona, route: q.get("route") } : null;
})();

function AuthGate() {
  // Re-render the whole screen tree when locale/timezone changes so every
  // date/time re-formats live.
  useDateTimeVersion();
  // The signed-in staff member's own profile zone becomes the display zone
  // (explicit user preference still wins) — 2026-08-07, Pat.
  useStaffTimezoneSync();
  const { signedIn, resolvingLanding } = useAuth();
  const { route, go } = useNav();
  // Deep-link auto-sign-in (demo): a link like index.html?persona=<id>&route=<routeId>
  // (used by the RFE board's login tags) mints that persona's session and lands
  // on the target route, skipping the org/persona chooser. // ui-config
  // Same sequencing as Signin.pick() (2026-09-17): `signIn()` raises the
  // resolving-landing gate, the served landing is awaited, and the gate clears
  // in `finally` — so a `?persona=` link can no more flash /dashboard than a
  // card press can. An explicit `route=` still wins over the served landing.
  const booted = useRef(false);
  useEffect(() => {
    if (booted.current || !DEEPLINK || signedIn) return;
    booted.current = true;
    (async () => {
      try {
        await signIn(DEEPLINK.persona);
        void ensureTenantContext();
        const target = DEEPLINK.route || (await resolveLanding(getUser())).route;
        go(target);
      } catch { /* stay on the chooser */ }
      finally { landingResolved(); }
    })();
  }, [signedIn, go]);
  // 2026-07-14 docket (attend-host flip): a public event route on the attend
  // host renders ANONYMOUSLY — no session, no persona chooser. attend.<tenant>
  // is the public WYSIWYG face of a published event; studio.<tenant> (and every
  // operator route on any host) stays SSO-gated exactly as before.
  // 2026-08-11 (JAI-562): decide on the HOST FIRST. The check below used to be
  // host-AND-route, which fails OPEN: on a bare `attend.<tenant>/` the path is
  // empty, pathToEntry() falls back to "dashboard", that is not in
  // PUBLIC_ATTEND_ROUTES, and control dropped through to <Signin/> — so a
  // public hostname rendered the operator persona chooser. Worse, picking a
  // persona flipped `signedIn` and handed back the full operator <AppRouter/>
  // on that same public host.
  //
  // The four sibling public hosts (book./join./meet./public.) were never
  // covered at all, because isAttendHost() matches only `attend` — they showed
  // the chooser on EVERY path. They have no public surface yet, so they now
  // correctly render the public not-found instead of a way in.
  //
  // Host-first makes the failure mode closed: on a public host the only thing
  // reachable is an allowlisted public route. Operator hosts are untouched.
  if (isPublicHost()) return isPublicAttendRoute(route) ? <AppRouter /> : <PublicNotFound />;
  /* `resolvingLanding` (2026-09-17): signed in, but the landing route is not yet
     applied. Rendering the router here mounted Dashboard at the boot default —
     the ~20 admin fetches, the two permission banners and the phone launcher's
     "No apps are enabled" flash all came from this one line. Signin stays
     mounted and shows the step overlay until nav.reset() has fired. */
  if (!signedIn || resolvingLanding) return <Signin />;
  /* The acting-as banner mounts HERE, at the app root, not in `Screen`.
     Mounted in `Screen` it missed every view that doesn't use it — including
     the app launcher's "No apps are enabled for your role yet" shell, which is
     exactly where an impersonated narrow-role persona lands. The admin was left
     with no banner and no exit but Sign out, which is the whole bug this was
     meant to fix. The narrower the target's role, the more certain the banner
     was to be missing. */
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      <ImpersonationBanner />
      <div style={{ flex: 1, minHeight: 0 }}><AppRouter /></div>
    </div>
  );
}

/**
 * Public 404. A visitor on a public host who asks for anything outside the
 * public allowlist gets this — deliberately content-free: no product nav, no
 * sign-in affordance, nothing that hints at an operator surface behind it.
 */
function PublicNotFound() {
  return (
    <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 40, fontFamily: "var(--jrni-font-family-sans)" }}>
      <div style={{ maxWidth: 420, textAlign: "center" }}>
        <div style={{ fontSize: 19, fontWeight: 700, color: "var(--jrni-color-neutral-1)", marginBottom: 8 }}>Page not found</div>
        <div style={{ fontSize: 14, color: "var(--jrni-color-neutral-2)", lineHeight: 1.5 }}>This page isn't available.</div>
      </div>
    </div>
  );
}

/**
 * §8 (2026-08-19) — the app root carries `data-theme` so dark can be RENDERED at all.
 * Until this line the app had never been drawn in dark: every token pair was in
 * `tokens.css`, and nothing set the attribute they hang off.
 *
 * It runs at MODULE level here rather than in `main.tsx` because the preview host
 * builds its own entry that imports THIS file and calls `createRoot` itself — a line in
 * main.tsx never executes there, which is how the first attempt went unnoticed.
 *
 * NO user-facing toggle in this pass, deliberately: the goal is a testable build.
 * `document.documentElement.setAttribute("data-theme", "dark")` in the console flips it,
 * and an attribute already set by a host page or a screenshot harness is left alone.
 */
if (typeof document !== "undefined" && !document.documentElement.hasAttribute("data-theme")) {
  document.documentElement.setAttribute("data-theme", "light");
}

export default function App() {
  return (
    <div data-jrni-component="studio-app-root" style={{ height: "100vh", overflow: "hidden", background: "var(--jrni-color-surface-canvas)" }}>
      {/* Last-resort boundary: catches anything thrown OUTSIDE a screen (the
          rail, topbar, router or auth gate itself) so the app can never render
          a bare grey page with no way back. Per-screen boundaries live in
          AppRouter and handle the common case without losing the chrome. */}
      <ErrorBoundary scope="app" label="Studio" variant="app">
        <NavProvider initialRoute="dashboard">
          <AuthGate />
        </NavProvider>
      </ErrorBoundary>
      <ToastHost />
    </div>
  );
}
